import {
  AddMachineOptionDto,
  MachineQuotationItemDto,
  MachineQuotationItemUpdateDto,
  MachinesApiService,
  QuotationDto
} from '@api/machines'
import {
  GENERAL_MACHINE_TYPES,
  handleCatchError,
  Options,
  optionChangeType,
  type StockTypes
} from '@common/utils'
import { successToast } from '@components/reusable'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery
} from '@tanstack/react-query'
import { useLocation, useParams } from 'react-router-dom'
import { AddCustomFactoryFitFunctionProps } from '@/components'
import { CUSTOMER_SPEC_KEYS } from '@/pages/customers/queries/customerSpecsQueries'
import { useEquipmentBuilderStore as SpecsBuilderStore } from '@/pages/customers/stores/equipmentBuilderStore'
import { SectionsErrorsType } from '@/pages/customers/utils/interfaces'
import { useWarranty } from '@/pages/quotation-tool/stores/builder/hooks'
import {
  HandleOptionChangeParamsType,
  RulesToBeAppliedListType
} from '@/pages/quotation-tool/utils/types'
import { useLeaveGuardStore } from '@/stores/leaveGuardStore'

const QUOTE_QUERY_KEYS = {
  QUOTE: 'quote',
  QUOTE_APPROVERS: 'quoteApprovers',
  QUOTE_SPECS_BY_CUSTOMER: 'quoteSpecsByCustomer',
  QUOTE_SPEC_BY_SPEC_ID: 'quoteSpecBySpecId',
  QUOTE_ARCHIVED_EQUIPMENTS: 'quoteArchivedEquipments',
  QUOTE_TRADE_IN_TYPES: 'quoteTradeInTypes',
  QUOTE_MANUFACTURER_LIST: 'quoteManufacturerList'
}

export const MACHINES_QUERY_KEYS = {
  GET_BRAND_BY_EQ_TYPE: 'getBrandByEqType',
  GET_STOCK_MACHINES_BY_BRAND: 'getStockMachinesByBrand',
  GET_STOCK_ATTACHMENTS_BY_BRAND: 'getStockAttachmentsByBrand',
  GET_STOCK_ITEMS_BY_BRAND: 'getStockItemsByBrand',
  GET_STOCKS_BY_MACHINE_ATTACHMENT_ID: 'getStocksByMachineAttachmentId',
  GET_STOCKS_BY_MACHINE_ID: 'getStocksByMachineId',
  GET_STOCK_ITEMS_BY_ID_AND_STOCK: 'getStockItemsByIdAndStock',
  GET_MACHINES_BY_BRAND: 'getMachinesByBrand',
  GET_ATTACHMENTS_BY_BRAND: 'getAttachmentsByBrand',
  GET_MACHINE_BY_ID: 'getMachineById',
  GET_COMPATIBLE_MACHINES: 'getCompatibleMachines',
  GET_AVAILABLE_REPLACEMENTS: 'getAvailableReplacements',
  GET_ALL_BRANDS: 'allBrands'
}

export const EQUIPMENTS_QUERY_KEYS = {
  GET_DRAFT_EQUIPMENT: 'getDraftEquipment'
}

export const MACHINE_TYPE = {
  ATTACHMENT: 'attachment',
  MACHINE: 'machine'
} as const

export type MachineTypeValues = (typeof MACHINE_TYPE)[keyof typeof MACHINE_TYPE]

export type EquipmentType = 'attachment' | 'machine'

const brandsQueryConfig = {
  queryKey: [MACHINES_QUERY_KEYS.GET_ALL_BRANDS],
  queryFn: async () => {
    try {
      return await MachinesApiService.allbrands()
    } catch (error) {
      handleCatchError(error)
    }
  },
  staleTime: 1000 * 60 * 60 // 60 minutes
}

export const useGetMachineCache = (enableCall: boolean = false) => {
  const { quoteId, specId, equipmentId } = useParams()

  const isQuoteItem = Boolean(quoteId)

  return useQuery({
    queryKey: [
      EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
      isQuoteItem ? quoteId : specId,
      equipmentId
    ],
    queryFn: async () => {
      try {
        return await MachinesApiService.quotationMachineGetDraft(
          String(quoteId),
          String(equipmentId)
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    refetchOnMount: 'always',
    enabled: enableCall
  })
}

export const useClearEquipmentCache = () => {
  const queryClient = useQueryClient()
  const { equipmentId, quoteId, specId } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  const isQuoteItem = Boolean(quoteId)

  return useMutation({
    mutationFn: async () => {
      try {
        return await MachinesApiService.quotationMachineClearDraft(
          String(equipmentId)
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    onSuccess: () => {
      setHasUnsavedChanges(false)

      if (!isQuoteItem) {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId]
        })
      }
    }
  })
}

export const useOnSaveMachine = () => {
  const queryClient = useQueryClient()
  const { quoteId, specId, equipmentId, id: customerId } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )
  const isQuoteItem = Boolean(quoteId)

  return useMutation({
    mutationFn: async ({
      specName,
      updateToNewPriceSaving,
      machineItemId
    }: {
      specName?: string
      updateToNewPriceSaving?: boolean
      machineItemId?: string
    }) => {
      const itemId = machineItemId ?? equipmentId
      try {
        if (isQuoteItem) {
          return await MachinesApiService.quotationMachineSaveDraft(
            String(quoteId),
            String(itemId),
            updateToNewPriceSaving
          )
        }

        const { id } =
          await MachinesApiService.specSaveQuotationMachineItemEditedToSpec(
            String(specId),
            String(itemId),
            Number(customerId),
            String(specName)
          )
        return id
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      successToast()
      setHasUnsavedChanges(false)

      if (isQuoteItem) {
        queryClient.invalidateQueries({
          queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
        })
      }
    }
  })
}

const getOptionCallWithRules = (option: Options) => {
  let fnCall

  switch (option) {
    case Options.ADD_FACTORY_FIT:
      fnCall = MachinesApiService.quotationMachineAddFactoryOption
      break
    case Options.ADD_DEALER_FIT:
      fnCall = MachinesApiService.quotationMachineAddDealerFitOption
      break
    default:
      fnCall = () => null
      break
  }

  return fnCall
}

const getOptionCallWithoutRules = (
  option: Options,
  useBrandWarranty?: boolean,
  isWarrantyNull?: boolean
) => {
  let fnCall

  switch (option) {
    case Options.REMOVE_FACTORY_FIT:
      fnCall = MachinesApiService.quotationMachineRemoveFactoryOption
      break
    case Options.ADD_DEALER_FIT:
      fnCall = MachinesApiService.quotationMachineAddDealerFitOption
      break
    case Options.REMOVE_DEALER_FIT:
      fnCall = MachinesApiService.quotationMachineRemoveDealerFitOption
      break
    case Options.ADD_WARRANTY:
      if (useBrandWarranty) {
        fnCall = MachinesApiService.quotationMachineBrandWarranty
      } else {
        fnCall = MachinesApiService.quotationMachineAddWarranty
      }
      break
    case Options.REMOVE_WARRANTY:
      if (useBrandWarranty || isWarrantyNull) {
        fnCall = MachinesApiService.quotationMachineBrandWarranty1
      } else {
        fnCall = MachinesApiService.quotationMachineRemoveWarranty
      }
      break
    case Options.ADD_TRANSPORT_OPTION:
      fnCall = MachinesApiService.quotationMachineAddTransportOption
      break
    case Options.REMOVE_TRANSPORT_OPTION:
      fnCall = MachinesApiService.quotationMachineRemoveTransportOption
      break
    case Options.ADD_SERVICE_CONTRACT:
      fnCall = MachinesApiService.quotationMachineAddServiceContract
      break
    case Options.REMOVE_SERVICE_CONTRACT:
      fnCall = MachinesApiService.quotationMachineRemoveServiceContract
      break
    default:
      fnCall = () => null
      break
  }

  return fnCall
}

const addFactoryFitOrDealerOption = async ({
  option,
  payload,
  setRulesToBeApplied
}: {
  option: Options
  payload: AddMachineOptionDto
  setRulesToBeApplied: (rules: RulesToBeAppliedListType) => void
}) => {
  const fnCall = getOptionCallWithRules(option)
  const response = (await fnCall(payload)) as MachineQuotationItemUpdateDto

  if (response?.rulesRequiredChoice) {
    setRulesToBeApplied?.({
      choice: response.rulesRequiredChoice,
      metadata: {
        option: option,
        optionId: Number(payload.optionId)
      }
    })
  }
  return response
}

const addWarrantyOrServiceContractOrTransport = async ({
  option,
  quotationId,
  equipmentId,
  optionId,
  sectionsErrors,
  updateSectionsErrors,
  useBrandWarranty,
  isWarrantyNull
}: {
  option: Options
  quotationId: string
  equipmentId: string
  optionId: number
  sectionsErrors: SectionsErrorsType
  updateSectionsErrors: (errorFlag: keyof SectionsErrorsType) => void
  useBrandWarranty?: boolean
  isWarrantyNull?: boolean
}) => {
  const fnCall = getOptionCallWithoutRules(
    option,
    useBrandWarranty,
    isWarrantyNull
  )
  const response = await fnCall(
    String(quotationId),
    String(equipmentId),
    optionId
  )

  if (response) {
    option === Options.ADD_WARRANTY &&
      sectionsErrors.warranty &&
      updateSectionsErrors('warranty')
    option === Options.ADD_SERVICE_CONTRACT &&
      sectionsErrors.serviceContract &&
      updateSectionsErrors('serviceContract')
    option === Options.ADD_TRANSPORT_OPTION &&
      sectionsErrors.transportOption &&
      updateSectionsErrors('transportOption')
  }

  return response
}

export const useHandleMachineOptionChange = ({
  useBrandWarranty
}: {
  useBrandWarranty?: boolean
} = {}) => {
  const queryClient = useQueryClient()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isEditingItem = queryParams.get('edit')
  const { id: customerId, quoteId, specId, equipmentId } = useParams()

  const isQuoteItem = Boolean(quoteId)
  const isSpecItem = Boolean(specId)

  const sectionsErrors = SpecsBuilderStore(state => state.sectionsErrors)
  const updateSectionsErrors = SpecsBuilderStore(
    state => state.updateSectionErrors
  )
  const setRulesToBeAppliedSpecs = SpecsBuilderStore(
    state => state.setRulesToBeApplied
  )
  const { setRulesToBeApplied: setRulesToBeAppliedQuotes } = useWarranty()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({
      groupId,
      option,
      optionId
    }: HandleOptionChangeParamsType) => {
      let machineItem
      if (isQuoteItem) {
        const cachedData = queryClient.getQueryData([
          EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
          quoteId,
          equipmentId
        ]) as MachineQuotationItemUpdateDto

        const { machineQuotationItem } = cachedData
        machineItem = machineQuotationItem

        if (typeof machineQuotationItem?.quotationItemId !== 'string') {
          throw new TypeError('Invalid quotationItemId')
        }
      }

      if (
        optionChangeType(option).addFactoryFit ||
        optionChangeType(option).addDealerFit
      ) {
        const payload: AddMachineOptionDto = {
          itemId: equipmentId,
          optionId,
          quotationId: isQuoteItem ? String(quoteId) : String(specId),
          ...(groupId && { userOptions: groupId })
        }

        return await addFactoryFitOrDealerOption({
          option,
          payload,
          setRulesToBeApplied: isSpecItem
            ? setRulesToBeAppliedSpecs
            : setRulesToBeAppliedQuotes
        })
      }

      const isWarrantyNull = machineItem?.warrantyItem === null

      return (await addWarrantyOrServiceContractOrTransport({
        option,
        quotationId: isQuoteItem ? String(quoteId) : String(specId),
        equipmentId: String(equipmentId),
        optionId,
        sectionsErrors,
        updateSectionsErrors,
        useBrandWarranty,
        isWarrantyNull
      })) as MachineQuotationItemUpdateDto
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: response => {
      setHasUnsavedChanges(true)
      successToast()

      const updatedItem =
        response.machineQuotationItem as MachineQuotationItemDto

      if (isSpecItem && isEditingItem) {
        // rehydrate cached data for Spec Template Machine
        queryClient.setQueryData<QuotationDto>(
          [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId, customerId, equipmentId],
          (oldData?: QuotationDto) => {
            if (!oldData) {
              return
            }

            return {
              ...oldData,
              machineItems: [updatedItem]
            }
          }
        )
      } else {
        // rehydrate cached data for Quote/Spec Draft Machine
        queryClient.setQueryData<MachineQuotationItemUpdateDto>(
          [
            EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
            isSpecItem ? specId : quoteId,
            equipmentId
          ],
          (oldData?: MachineQuotationItemUpdateDto) => {
            if (!oldData || !response.machineQuotationItem) {
              return
            }

            return {
              ...oldData,
              machineQuotationItem: response.machineQuotationItem,
              machineItemPriceOld: response.machineItemPriceOld,
              rulesRequiredChoice: response.rulesRequiredChoice
            }
          }
        )
      }
    }
  })
}

export const useAddCustomFactoryFitMachine = () => {
  const queryClient = useQueryClient()
  const { quoteId, specId, equipmentId } = useParams()
  const isQuoteItem = Boolean(quoteId)
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({
      isUpdating,
      name,
      optionId,
      dnp
    }: AddCustomFactoryFitFunctionProps) => {
      if (isUpdating) {
        return await MachinesApiService.quotationMachineUpdateCustomFactoryOption(
          {
            machineItemId: String(equipmentId),
            quotationId: String(quoteId),
            optionId,
            name,
            dnp
          }
        )
      }

      return await MachinesApiService.quotationMachineAddCustomFactoryOption({
        machineItemId: String(equipmentId),
        quotationId: String(quoteId),
        optionId,
        name,
        dnp
      })
    },
    onError: error => {
      handleCatchError(error)
    },
    onSuccess(response) {
      setHasUnsavedChanges(true)
      successToast()

      queryClient.setQueryData<MachineQuotationItemUpdateDto>(
        [
          EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
          isQuoteItem ? quoteId : specId,
          equipmentId
        ],
        oldData => ({
          ...oldData,
          machineQuotationItem: response.machineQuotationItem,
          machineItemPriceOld: response.machineItemPriceOld ?? {}
        })
      )
    }
  })
}

export const useAddCustomDealerFitMachine = () => {
  const queryClient = useQueryClient()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isEditingSpecItem = queryParams.get('edit')
  const { id: customerId, quoteId, specId, equipmentId } = useParams()

  const isQuoteItem = Boolean(quoteId)
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({
      isUpdating,
      name,
      optionId,
      dnp
    }: AddCustomFactoryFitFunctionProps) => {
      try {
        if (isUpdating) {
          return await MachinesApiService.quotationMachineUpdateCustomDealerFitOptions(
            {
              quotationId: isQuoteItem ? quoteId : specId,
              optionId,
              name,
              dnp,
              machineItemId: equipmentId
            }
          )
        }

        return await MachinesApiService.quotationMachineAddCustomDealerFitOptions(
          {
            quotationId: isQuoteItem ? quoteId : specId,
            optionId,
            name,
            dnp,
            machineItemId: equipmentId
          }
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess(response) {
      setHasUnsavedChanges(true)
      successToast()

      if (isEditingSpecItem && !isQuoteItem) {
        // rehydrate cached data for Spec Template Machine
        queryClient.setQueryData<MachineQuotationItemUpdateDto>(
          [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId, customerId, equipmentId],
          oldData => {
            if (!oldData) {
              return
            }

            return {
              ...oldData,
              machineItems: [response?.machineQuotationItem],
              machineAttachmentItems: [response?.machineQuotationItem]
            }
          }
        )
      } else {
        // rehydrate cached data for Quote/Spec Draft Machine
        queryClient.setQueryData<MachineQuotationItemUpdateDto>(
          [
            EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
            isQuoteItem ? quoteId : specId,
            equipmentId
          ],
          oldData => {
            if (!oldData) {
              return
            }

            return {
              ...oldData,
              machineQuotationItem: response?.machineQuotationItem,
              machineItemPriceOld: response?.machineItemPriceOld,
              rulesRequiredChoice: response?.rulesRequiredChoice
            }
          }
        )
      }
    }
  })
}

export const useUpdateDealerFitQuantityMachine = () => {
  const queryClient = useQueryClient()
  const { quoteId, specId, equipmentId } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )
  const isQuoteItem = Boolean(quoteId)

  return useMutation({
    mutationFn: async ({
      actionType,
      itemQuantity,
      optionId
    }: {
      actionType: 'decrease' | 'increase'
      itemQuantity: number
      optionId: number
    }) => {
      if (!(quoteId || specId) && equipmentId) {
        return null
      }

      let auxQuantity = itemQuantity

      switch (actionType) {
        case 'increase':
          auxQuantity += 1
          break
        case 'decrease':
          auxQuantity -= 1
          break
        default:
          break
      }

      return await MachinesApiService.quotationMachineUpdateDealerFitOptionQuantity(
        isQuoteItem ? String(quoteId) : String(specId),
        String(equipmentId),
        optionId,
        auxQuantity
      )
    },
    onSuccess: response => {
      if (!response) {
        return null
      }

      successToast()
      setHasUnsavedChanges(true)

      queryClient.setQueryData<MachineQuotationItemUpdateDto>(
        [
          EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
          isQuoteItem ? String(quoteId) : String(specId),
          String(equipmentId)
        ],
        oldData => ({
          ...oldData,
          machineQuotationItem: response.machineQuotationItem,
          machineItemPriceOld: response.machineItemPriceOld ?? {},
          factoryOptionItems: response.machineQuotationItem?.factoryOptionItems
        })
      )
    },
    onError: error => {
      handleCatchError(error)
    }
  })
}

export const useHandleUpdateMachineNotes = () => {
  const queryClient = useQueryClient()
  const { quoteId, specId, equipmentId } = useParams()

  const isQuoteItem = Boolean(quoteId)

  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({ notes }: { notes: string }) => {
      return await MachinesApiService.quotationMachineUpdateNotes(
        String(isQuoteItem ? quoteId : specId),
        String(equipmentId),
        notes
      )
    },
    throwOnError: false,
    onSuccess: () => {
      setHasUnsavedChanges(true)
      successToast()

      queryClient.invalidateQueries({
        queryKey: [
          EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
          isQuoteItem ? quoteId : specId
        ]
      })

      if (!isQuoteItem) {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId]
        })
      }
    },
    onError: error => handleCatchError(error)
  })
}

export const useGetBrandByEquipmentType = (eqType: EquipmentType) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_BRAND_BY_EQ_TYPE, eqType],
    queryFn: async () => {
      try {
        /* istanbul ignore else */
        if (eqType) {
          return await MachinesApiService.brandGetBrandsByType(eqType)
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!eqType,
    throwOnError: false,
    gcTime: 0,
    staleTime: 0
  })
}

export const useGetCatalogAndStockAttachmentsBrandsForMachine = (
  machineId: number
) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_BRAND_BY_EQ_TYPE, machineId],
    queryFn: async () => {
      try {
        return await MachinesApiService.getCatalogAndStockAttachmentsBrandsForMachine(
          machineId
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!machineId,
    throwOnError: false
  })
}

export const useGetStockItemsByBrandId = ({
  brandId,
  machineType,
  tonnage
}: {
  brandId?: number
  machineType?: MachineTypeValues
  tonnage?: number
}) => {
  return useQuery({
    queryKey: [
      MACHINES_QUERY_KEYS.GET_STOCK_ITEMS_BY_BRAND,
      brandId,
      machineType,
      tonnage
    ],
    queryFn: async () => {
      /* istanbul ignore if */
      if (!brandId) {
        return []
      }

      try {
        if (machineType === MACHINE_TYPE.MACHINE) {
          return await MachinesApiService.stocksGetMachinesByBrandId(brandId)
        } else if (machineType === MACHINE_TYPE.ATTACHMENT) {
          return await MachinesApiService.stocksGetMachineAttachmentsByBrandId(
            brandId,
            tonnage
          )
        }
      } catch (error) {
        handleCatchError(error)
        return []
      }
    },
    enabled: !!brandId,
    refetchOnMount: true,
    staleTime: 0,
    throwOnError: false
  })
}

export const useGetStockMachinesByBrandId = (
  brandId?: number,
  eqType?: MachineTypeValues
) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_STOCK_MACHINES_BY_BRAND, brandId],
    queryFn: async () => {
      try {
        /* istanbul ignore else */
        if (brandId) {
          return await MachinesApiService.stocksGetMachinesByBrandId(brandId)
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!brandId && eqType === 'machine',
    throwOnError: false
  })
}

export const useGetStockAttachmentsByBrandId = (
  brandId: number,
  eqType: MachineTypeValues
) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_STOCK_ATTACHMENTS_BY_BRAND, brandId],
    queryFn: async () => {
      try {
        return await MachinesApiService.stocksGetMachineAttachmentsByBrandId(
          brandId
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!brandId && eqType === 'attachment',
    throwOnError: false
  })
}

export const useGetStockItemsByIdAndStock = ({
  stockType,
  machineType,
  id
}: {
  stockType: StockTypes
  machineType?: MachineTypeValues
  id?: number
}) => {
  return useQuery({
    queryKey: [
      MACHINES_QUERY_KEYS.GET_STOCK_ITEMS_BY_ID_AND_STOCK,
      id,
      stockType,
      machineType
    ],
    queryFn: async () => {
      try {
        /* istanbul ignore else */
        if (id) {
          if (machineType === 'machine') {
            return await MachinesApiService.stocksGetStocksByMachineId(
              id,
              stockType
            )
          } else if (machineType === 'attachment') {
            return await MachinesApiService.stocksGetStocksByMachineAttachmentId(
              id,
              stockType
            )
          }
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!id
  })
}

export const useGetAvailableReplacements = ({
  quotationItemdId,
  machineType,
  enabled
}: {
  quotationItemdId?: number
  machineType: (typeof GENERAL_MACHINE_TYPES)[keyof typeof GENERAL_MACHINE_TYPES]
  enabled?: boolean
}) => {
  return useQuery({
    queryKey: [
      MACHINES_QUERY_KEYS.GET_AVAILABLE_REPLACEMENTS,
      quotationItemdId
    ],
    queryFn: async () => {
      try {
        const isMachine = machineType === GENERAL_MACHINE_TYPES.MACHINE
        const apiCall = isMachine
          ? MachinesApiService.stocksMachinesReplacement
          : MachinesApiService.stocksAttachmentReplacement

        if (quotationItemdId) {
          return await apiCall(quotationItemdId)
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: enabled
  })
}

export const useGetAttachmentsByIdAndStock = (
  stockStatus: StockTypes,
  machineAttachmentId?: number
) => {
  return useQuery({
    queryKey: [
      MACHINES_QUERY_KEYS.GET_STOCK_ATTACHMENTS_BY_BRAND,
      machineAttachmentId,
      stockStatus
    ],
    queryFn: async () => {
      try {
        /* istanbul ignore else */
        if (machineAttachmentId) {
          return await MachinesApiService.stocksGetStocksByMachineAttachmentId(
            machineAttachmentId,
            stockStatus
          )
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!machineAttachmentId,
    throwOnError: false
  })
}

export const useGetMachinesByIdAndStock = (
  stockStatus: StockTypes,
  machineId?: number
) => {
  return useQuery({
    queryKey: [
      MACHINES_QUERY_KEYS.GET_STOCKS_BY_MACHINE_ID,
      machineId,
      stockStatus
    ],
    queryFn: async () => {
      try {
        /* istanbul ignore else */
        if (machineId) {
          return await MachinesApiService.stocksGetStocksByMachineId(
            machineId,
            stockStatus
          )
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !!machineId,
    throwOnError: false
  })
}

export const useFetchMachinesByBrandName = ({
  brandName
}: {
  brandName: string
}) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_MACHINES_BY_BRAND, brandName],
    queryFn: async () => {
      try {
        return await MachinesApiService.getMachinesByBrandName(brandName)
      } catch (err) {
        handleCatchError(err)
      }
    },
    throwOnError: false,
    enabled: !!brandName
  })
}

export const useFetchAttachmentsByBrandName = ({
  brandName
}: {
  brandName: string
}) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_ATTACHMENTS_BY_BRAND, brandName],
    queryFn: async () => {
      try {
        return await MachinesApiService.getMachineAttachmentsByBrandName(
          brandName
        )
      } catch (err) {
        handleCatchError(err)
      }
    },
    throwOnError: false,
    enabled: !!brandName
  })
}

export const useGetMachineById = ({
  machineId,
  withArchived
}: {
  machineId: number
  withArchived: boolean
}) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_MACHINE_BY_ID, machineId],
    queryFn: async () => {
      try {
        return await MachinesApiService.getMachineById(machineId, withArchived)
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: Boolean(machineId),
    refetchOnMount: 'always',
    throwOnError: false
  })
}

export const useValidateMachinesByTemplates = () => {
  return useMutation({
    mutationFn: async ({
      itemId,
      machineIds
    }: {
      itemId?: number
      machineIds: number[]
    }) => {
      return await MachinesApiService.machineValidateMachinesByTemplates({
        itemId,
        machineIds
      })
    },
    throwOnError: false,
    onError: error => {
      handleCatchError(error)
    },
    onSuccess: () => {
      successToast()
    }
  })
}

export const useValidateAttachmentsByTemplates = () => {
  return useMutation({
    mutationFn: async ({
      itemId,
      attachmentIds
    }: {
      itemId?: number
      attachmentIds: number[]
    }) => {
      return await MachinesApiService.machineValidateAttachmentsByTemplates({
        itemId,
        attachmentIds
      })
    },
    throwOnError: false,
    onError: error => {
      handleCatchError(error)
    },
    onSuccess: () => {
      successToast()
    }
  })
}

export const useFetchCompatibleMachinesBySpecIdAndTemplateId = ({
  specId,
  templateId
}: {
  specId: string
  templateId: string
}) => {
  return useQuery({
    queryKey: [MACHINES_QUERY_KEYS.GET_COMPATIBLE_MACHINES, specId, templateId],
    queryFn: async () => {
      try {
        return await MachinesApiService.customersSpecsTemplateCompatibleMachines(
          specId,
          templateId
        )
      } catch (err) {
        handleCatchError(err)
      }
    },
    throwOnError: false,
    enabled: !!specId && !!templateId
  })
}

export const useGetAllBrands = (enabled: boolean = true) => {
  return useQuery({
    ...brandsQueryConfig,
    enabled,
    throwOnError: false
  })
}

export const useGetAllBrandsSuspense = () => {
  return useSuspenseQuery(brandsQueryConfig)
}
